import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { UitkFloatingFullWidthButton, UitkButtonSize } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { classNames, liveAnnounce, Level } from 'uitk-react-utils';
import { UitkToolbar, UitkToolbarButton } from 'uitk-react-toolbar';
import styles from './uitk-date-picker.css';
import { UitkDatePickerInput } from './uitk-date-picker-input.esm.js';
import { UitkCalendar } from './uitk-calendar.esm.js';

var getClassNameList = classNames.bind(styles);
var DatePickerDialogView = /** @class */ (function (_super) {
    __extends(DatePickerDialogView, _super);
    function DatePickerDialogView(props) {
        var _this = _super.call(this, props) || this;
        _this.uid = Math.random().toString(36).substring(7);
        _this.doneButtonRef = React.createRef();
        _this.state = {
            localizedText: {
                cancelButton: function () { return props.l10n.formatText('datePicker.cancelButton.a11yLabel'); },
                datePickerOpened: function () { return props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'); },
                doneButtonFare: function (nights, lowestFareMessage, startDate, endDate) {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
                    }
                    return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
                },
                clearInstructions: function () { return props.l10n.formatText('datePicker.clearInstructions.a11yLabel'); },
                doneInstructions: function () { return props.l10n.formatText('datePicker.doneInstructions.a11yLabel'); },
                hiddenDoneButton: function (nights, startDate, endDate) {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
                    }
                    return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
                },
                // The "nightSummary" function is written like this to support older versions of uitk-react-localization (pre HoB)
                // if you are graduating this component please change this function to
                // nightSummary: (nights: number) => props.l10n.formatText('datePicker.nightSummary', nights),
                // and ensure teams are on the latest version of uitk-react-localization
                nightSummary: function (nights) {
                    return props.l10n.formatText('datePicker.nightSummary', nights) ||
                        (nights <= 1
                            ? props.l10n.formatText('datePicker.nightSummary.single', nights)
                            : props.l10n.formatText('datePicker.nightSummary.multiple', nights));
                },
                skipToButton: function (buttonName) { return props.l10n.formatText('datePicker.skipToButton', buttonName); }
            },
            selection: props.selection
        };
        _this.onClickTopStart = _this.onClickTopStart.bind(_this);
        _this.onClickTopEnd = _this.onClickTopEnd.bind(_this);
        _this.onSubmitClick = _this.onSubmitClick.bind(_this);
        _this.onDismissClick = _this.onDismissClick.bind(_this);
        _this.onSelectDate = _this.onSelectDate.bind(_this);
        _this.internallyUpdateDatePicker = _this.internallyUpdateDatePicker.bind(_this);
        _this.onChangeStartDate = _this.onChangeStartDate.bind(_this);
        _this.onChangeEndDate = _this.onChangeEndDate.bind(_this);
        _this.hiddenDoneButton = _this.hiddenDoneButton.bind(_this);
        _this.skipToDoneButton = _this.skipToDoneButton.bind(_this);
        return _this;
    }
    DatePickerDialogView.prototype.componentDidMount = function () {
        var localizedText = this.state.localizedText;
        liveAnnounce(localizedText.datePickerOpened(), Level.ASSERTIVE);
    };
    DatePickerDialogView.prototype.shouldComponentUpdate = function (nextProps) {
        if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
            var selection = this.props.selection;
            selection.configureHolidays(nextProps.holidays);
        }
        return true;
    };
    DatePickerDialogView.prototype.onClickTopStart = function () {
        var onSelectStartMode = this.props.onSelectStartMode;
        onSelectStartMode();
        this.internallyUpdateDatePicker();
    };
    DatePickerDialogView.prototype.onClickTopEnd = function () {
        var onSelectEndMode = this.props.onSelectEndMode;
        onSelectEndMode();
        this.internallyUpdateDatePicker();
    };
    DatePickerDialogView.prototype.onSubmitClick = function () {
        var _a = this.props, onSubmit = _a.onSubmit, onDoneButtonClickCB = _a.onDoneButtonClickCB;
        if (onDoneButtonClickCB) {
            var selection = this.props.selection;
            var start = selection.start, end = selection.end;
            onDoneButtonClickCB(start, end);
        }
        else {
            onSubmit();
        }
    };
    DatePickerDialogView.prototype.onDismissClick = function () {
        var onDismiss = this.props.onDismiss;
        onDismiss();
    };
    DatePickerDialogView.prototype.onChangeStartDate = function (date) {
        var onChangeStartDate = this.props.onChangeStartDate;
        if (onChangeStartDate) {
            onChangeStartDate(date, true);
        }
    };
    DatePickerDialogView.prototype.onChangeEndDate = function (date) {
        var onChangeEndDate = this.props.onChangeEndDate;
        if (onChangeEndDate) {
            onChangeEndDate(date, false);
        }
    };
    /* Creating another state */
    DatePickerDialogView.prototype.onSelectDate = function () {
        this.internallyUpdateDatePicker();
    };
    // Workaround to having FullscreenDialog in the middle of the tree
    DatePickerDialogView.prototype.internallyUpdateDatePicker = function () {
        // eslint-disable-next-line react/no-access-state-in-setstate
        this.setState(this.state);
    };
    /* ********************** */
    DatePickerDialogView.prototype.skipToDoneButton = function () {
        var _this = this;
        var localizedText = this.state.localizedText;
        var onClick = function () {
            if (_this.doneButtonRef && _this.doneButtonRef.current) {
                _this.doneButtonRef.current.focus();
            }
        };
        return (React.createElement("button", { type: "button", className: getClassNameList('is-visually-hidden', 'uitk-date-picker-skip-to-done'), onClick: onClick }, localizedText.skipToButton(this.buttonText)));
    };
    DatePickerDialogView.prototype.hiddenDoneButton = function () {
        var _a = this.props, formattedStartSelection = _a.formattedStartSelection, formattedEndSelection = _a.formattedEndSelection, selection = _a.selection;
        var localizedText = this.state.localizedText;
        var nights = selection.numberOfNights;
        var startDate = formattedStartSelection;
        var endDate = formattedEndSelection;
        var visuallyHiddenText = localizedText.hiddenDoneButton(nights, startDate, endDate);
        return (React.createElement("button", { type: "button", className: getClassNameList('is-visually-hidden'), onClick: this.onSubmitClick }, visuallyHiddenText));
    };
    DatePickerDialogView.prototype.renderApplyButton = function (doneAnalytics, buttonText) {
        var localizedText = this.state.localizedText;
        var submitDisabled = this.props.submitDisabled;
        return (React.createElement(UitkFloatingFullWidthButton, { analytics: doneAnalytics, "data-stid": "apply-date-picker", disabled: submitDisabled, domRef: this.doneButtonRef, isFullWidth: true, onClick: this.onSubmitClick, size: UitkButtonSize.LARGE, "aria-label": localizedText.doneInstructions() }, buttonText));
    };
    DatePickerDialogView.prototype.render = function () {
        var _a = this.props, buttonText = _a.buttonText, clearButtonText = _a.clearButtonText, footerText = _a.footerText, closeAnalytics = _a.closeAnalytics, datePickerContent = _a.datePickerContent, doneAnalytics = _a.doneAnalytics, endSelectedMsg = _a.endSelectedMsg, firstDayOfWeek = _a.firstDayOfWeek, formattedStartSelection = _a.formattedStartSelection, lastValidDate = _a.lastValidDate, onScrollHandler = _a.onScrollHandler, selectionClassNames = _a.selectionClassNames, startSelectedMsg = _a.startSelectedMsg, singleMode = _a.singleMode, updateDatePicker = _a.updateDatePicker, labels = _a.labels, l10n = _a.l10n, holidays = _a.holidays, firstValidDate = _a.firstValidDate, useClearDates = _a.useClearDates, onClearDates = _a.onClearDates;
        this.buttonText = buttonText;
        var selection = this.state.selection;
        var startSelection = selection.start;
        var endSelection = selection.end;
        var isDateSelected = singleMode ? startSelection : startSelection && endSelection;
        var startSelectionFormatted = startSelection
            ? l10n.formatDate(startSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : formattedStartSelection;
        var formattedEndSelection = endSelection
            ? l10n.formatDate(endSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : l10n.formatText('datePicker.dateInput.endDate.placeholder');
        var localizedText = this.state.localizedText;
        var showHolidaysInfo = !!(selection.holidaysConfigured && selection.holidaysName !== '');
        var isScrim = !!(!footerText && !selection.holidaysConfigured);
        var scrimPaddingClassname = getClassNameList({
            'uitk-calendar-scrim': isScrim
        });
        var clearDatesButtonId = "clear-date-picker-".concat(this.uid);
        if (!selection.holidaysName) {
            selection.holidaysName = [];
        }
        return (React.createElement(UitkLayoutFlex, { direction: "column", className: getClassNameList('uitk-date-picker', 'date-picker-dialog') },
            React.createElement(UitkLayoutFlexItem, null,
                React.createElement(UitkToolbar, __assign({ className: getClassNameList('uitk-date-picker-toolbar'), navigationContent: {
                        navIconLabel: localizedText.cancelButton(),
                        onClick: this.onDismissClick,
                        onClickAnalytics: closeAnalytics
                    } }, (useClearDates && {
                    toolbarActionContent: (React.createElement(UitkToolbarButton, { "aria-label": localizedText.clearInstructions(), buttonId: clearDatesButtonId, disabled: !isDateSelected, onClick: onClearDates }, clearButtonText))
                })),
                    React.createElement(UitkLayoutFlex, { alignItems: "center", className: getClassNameList('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'), tag: "section" },
                        React.createElement(UitkLayoutFlexItem, null,
                            React.createElement(UitkDatePickerInput, { cssClassNames: selectionClassNames(true), isDialog: true, onClick: this.onClickTopStart, selectionDate: startSelectionFormatted, selectionDateMsg: "".concat(labels.start, " ").concat(startSelectedMsg), selection: selection.start, singleMode: singleMode })),
                        !singleMode && (React.createElement(React.Fragment, null,
                            React.createElement(UitkLayoutFlexItem, { className: getClassNameList('uitk-date-picker-selection-icon') },
                                React.createElement(UitkIcon, { name: "arrow_forward", size: UitkIconSize.SMALL })),
                            React.createElement(UitkLayoutFlexItem, null,
                                React.createElement(UitkDatePickerInput, { cssClassNames: selectionClassNames(false), isDialog: true, onClick: this.onClickTopEnd, selectionDate: formattedEndSelection, selectionDateMsg: "".concat(labels.end, " ").concat(endSelectedMsg), selection: selection.end })))),
                        this.hiddenDoneButton()))),
            React.createElement(UitkLayoutFlexItem, null,
                React.createElement(UitkCalendar, { buttonText: buttonText, className: scrimPaddingClassname, firstMonth: firstValidDate, skipToDoneButton: this.skipToDoneButton, datePickerContent: datePickerContent, isDialog: true, selection: selection, selectionStart: selection.start, selectionEnd: selection.end, startDate: firstValidDate, endDate: lastValidDate, updateDatePicker: updateDatePicker, firstDayOfWeek: firstDayOfWeek, onSelectDate: this.onSelectDate, singleMode: singleMode, holidays: holidays, onChangeStartDate: this.onChangeStartDate, onChangeEndDate: this.onChangeEndDate, onScrollHandler: onScrollHandler })),
            React.createElement(UitkLayoutFlexItem, null,
                isScrim && (React.createElement(UitkScrim, { type: UitkScrimType.BUTTON, className: getClassNameList('uitk-date-picker-footer') }, this.renderApplyButton(doneAnalytics, buttonText))),
                !isScrim && (React.createElement("div", { className: getClassNameList('uitk-date-picker-footer-opaque') },
                    showHolidaysInfo && (React.createElement("div", { className: getClassNameList('uitk-date-picker-footer-text') },
                        React.createElement("p", null, selection.holidaysName))),
                    footerText && (React.createElement("p", { className: getClassNameList('uitk-date-picker-footer-text', {
                            'uitk-date-picker-footer-text-center': !showHolidaysInfo
                        }) }, footerText)),
                    this.renderApplyButton(doneAnalytics, buttonText))))));
    };
    DatePickerDialogView.displayName = 'UitkDatePickerDialogView';
    return DatePickerDialogView;
}(React.Component));
var UitkDatePickerDialogView = function (props) {
    var l10n = useLocalization();
    return React.createElement(DatePickerDialogView, __assign({ l10n: l10n }, props));
};
UitkDatePickerDialogView.displayName = 'UitkDatePickerDialogView';
var UitkNewDatePickerDialogView = UitkDatePickerDialogView;

export { UitkDatePickerDialogView, UitkNewDatePickerDialogView };
